import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { assessmentAPI } from '../services/api';
import { Assessment } from '../types';
import MarkdownRenderer from '../components/MicroLearning/MarkdownRenderer';
import ContentFigure from '../components/MicroLearning/ContentFigure';
import PassagePanel from '../components/PassagePanel';
import './TakeAssessment.css';

interface Question {
  id: string;
  question_text: string;
  options: { [key: string]: string };
  correct_answer?: string;
  topic?: string;
  subtopic?: string;
  difficulty?: string;
  /** The chart or diagram this question asks about, when it has one. Six of
   *  the eighty authored UAT knowledge-check items do; no ESLCE question
   *  does, so this is absent for them. */
  figure?: { alt?: string; svg?: string } | null;
  /** The reading passage, ordering rules or chart caption this question shares
   *  with the others in its group. Repeated on each question in the group, so
   *  a student is never asked about a passage they cannot see. */
  passage?: string | null;
}

const TakeAssessment: React.FC = () => {
  const { student } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const assessment: Assessment = location.state?.assessment;
  /** Set when this check was started from inside a package. Every "back" and
   *  "exit" on this screen has to return there rather than to the ESLCE
   *  dashboard — a student who took a UAT check and pressed Back was landing
   *  on a different exam. */
  const packageKey: string | undefined = location.state?.packageKey;
  const homeRoute = packageKey ? `/p/${packageKey}/lessons` : '/dashboard';
  const selectedTopics: string[] = location.state?.selectedTopics || [];
  const isInitial: boolean = location.state?.isInitial || false;
  const isDiagnostic: boolean = location.state?.isDiagnostic || false;

  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<{ [questionId: string]: string }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRemaining, setTimeRemaining] = useState(-1);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [questionStartTime, setQuestionStartTime] = useState<number>(Date.now());
  const [questionTimes, setQuestionTimes] = useState<{ [questionId: string]: number }>({});

  useEffect(() => {
    if (!assessment) {
      navigate(homeRoute);
      return;
    }
    loadQuestions();
    const timerDuration = assessment.question_count * 90;
    setTimeRemaining(timerDuration);
  }, [assessment]);

  useEffect(() => {
    if (timeRemaining > 0 && !isSubmitted) {
      const timer = setTimeout(() => {
        setTimeRemaining(timeRemaining - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeRemaining === 0 && !isSubmitted && questions.length > 0) {
      handleSubmitAssessment();
    }
  }, [timeRemaining, isSubmitted, questions.length]);

  const loadQuestions = async () => {
    try {
      setLoading(true);
      const response = await assessmentAPI.getAssessmentQuestions(assessment.id);
      let filteredQuestions = response.questions;
      if (selectedTopics.length > 0) {
        filteredQuestions = response.questions.filter((q: Question) =>
          selectedTopics.includes(q.topic || '') || selectedTopics.includes(q.subtopic || '')
        );
      }
      setQuestions(filteredQuestions);
    } catch (err: any) {
      setError('Failed to load questions');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (questionId: string, answer: string) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const recordQuestionTime = () => {
    const elapsed = Math.round((Date.now() - questionStartTime) / 1000);
    const qId = questions[currentQuestionIndex]?.id;
    if (qId) {
      setQuestionTimes(prev => ({ ...prev, [qId]: (prev[qId] || 0) + elapsed }));
    }
  };

  const goToQuestion = (index: number) => {
    recordQuestionTime();
    setCurrentQuestionIndex(index);
    setQuestionStartTime(Date.now());
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) goToQuestion(currentQuestionIndex + 1);
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) goToQuestion(currentQuestionIndex - 1);
  };

  const handleSubmitAssessment = async () => {
    setIsSubmitted(true);
    try {
      recordQuestionTime();
      const responses = questions.map(question => ({
        question_id: question.id,
        answer: answers[question.id] || '',
        time_spent: questionTimes[question.id] || 0
      }));
      const submissionData = {
        assessment_id: assessment.id,
        session_type: isDiagnostic ? 'diagnostic' : (isInitial ? 'initial' : 'practice'),
        selected_topics: selectedTopics,
        responses,
        time_spent_seconds: assessment.question_count * 90 - timeRemaining,
        is_initial: isInitial
      };
      const result = await assessmentAPI.submitAssessment(assessment.id, submissionData);
      const score = calculateScore();
      navigate('/assessment-results', {
        state: {
          assessment, questions, answers, score,
          isInitial, isDiagnostic, selectedTopics,
          // Forwarded, so the results screen's exits go the same way.
          packageKey,
          backendResults: result.results || null,
          sessionId: result.results?.session_id
        }
      });
    } catch (error) {
      setError('Failed to submit assessment. Please try again.');
      setIsSubmitted(false);
    }
  };

  const calculateScore = () => {
    let correct = 0;
    questions.forEach(question => {
      if (answers[question.id] === question.correct_answer) correct++;
    });
    return { correct, total: questions.length, percentage: Math.round((correct / questions.length) * 100) };
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getAnsweredCount = () => Object.values(answers).filter(a => a && a !== 'NOT_COVERED').length;
  const getNotCoveredCount = () => Object.values(answers).filter(a => a === 'NOT_COVERED').length;
  const getTotalRespondedCount = () => Object.keys(answers).length;

  if (!assessment) {
    return (
      <div className="take-assessment-container">
        <div className="empty-state">
          <h2>Assessment Not Found</h2>
          <p>The assessment you're trying to access could not be found.</p>
          <button onClick={() => navigate(homeRoute)} className="btn-primary">Back to Dashboard</button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="take-assessment-container">
        <div className="empty-state">
          <div className="spinner"></div>
          <p>Loading questions...</p>
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="take-assessment-container">
        <div className="empty-state">
          <h2>No Questions Available</h2>
          <p>There are no questions available for the selected topics.</p>
          <button onClick={() => navigate(packageKey ? homeRoute : '/initial-assessment')} className="btn-primary">Back</button>
        </div>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className="take-assessment-container">
        <div className="empty-state">
          <div className="spinner"></div>
          <p>Submitting...</p>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const currentAnswer = answers[currentQuestion.id];

  // "Passage · question 2 of 4". A student who meets the same passage four
  // times in a row should be told it is the same one and how far through the
  // set they are, rather than left wondering whether it changed.
  const passagePosition = (() => {
    const text = currentQuestion.passage;
    if (!text) return null;
    const group = questions.filter((q) => q.passage === text);
    if (group.length < 2) return null;
    return {
      index: group.findIndex((q) => q.id === currentQuestion.id) + 1,
      total: group.length,
    };
  })();

  return (
    <div className="take-assessment-container">
      {/* Compact sticky header */}
      <header className="ka-header">
        <div className="ka-header-top">
          <button className="ka-exit" onClick={() => navigate(homeRoute)}>✕</button>
          <div className="ka-title">{assessment.title?.replace(/^Knowledge Check\s*-\s*/i, '')}</div>
          <div className={`ka-timer ${timeRemaining < 300 ? 'warning' : ''}`}>
            {formatTime(timeRemaining)}
          </div>
        </div>
        <div className="ka-progress-track">
          <div className="ka-progress-fill" style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }} />
        </div>
      </header>

      {/* Question dots — compact, scrollable */}
      <div className="ka-dots">
        {questions.map((q, i) => {
          const a = answers[q.id];
          const cls = i === currentQuestionIndex ? 'current'
            : a === 'NOT_COVERED' ? 'skipped'
            : a ? 'done' : '';
          return (
            <button key={i} className={`ka-dot ${cls}`} onClick={() => goToQuestion(i)}>
              {i + 1}
            </button>
          );
        })}
      </div>

      {/* Question content */}
      <main className="ka-body">
        <div className="ka-meta">
          <span className="ka-qnum">Q{currentQuestionIndex + 1}/{questions.length}</span>
        </div>

        {/* Above the question and visually apart from it. Twenty of the
            eighty authored items belong to a group that shares one of these,
            and until the passage was carried through they were asked cold —
            "What is the main idea of the passage?" with no passage. */}
        <PassagePanel
          text={currentQuestion.passage}
          cacheKey={`kc:${currentQuestion.id}`}
          surface="knowledge_check"
          position={passagePosition}
          trackProps={{ question_id: currentQuestion.id, topic: currentQuestion.topic }}
        />

        <div className="ka-question">
          <MarkdownRenderer>{currentQuestion.question_text}</MarkdownRenderer>
          {/* Drawn straight after the stem: these questions say "as shown" or
              "according to the chart", so the figure has to be between the
              question and the options, not below them. */}
          {currentQuestion.figure && <ContentFigure figure={currentQuestion.figure} />}
        </div>

        <div className="ka-options">
          {Object.entries(currentQuestion.options).map(([key, value]) => (
            <button
              key={key}
              className={`ka-option ${currentAnswer === key ? 'selected' : ''}`}
              onClick={() => handleAnswerSelect(currentQuestion.id, key)}
            >
              <span className="ka-option-letter">{key}</span>
              <span className="ka-option-text"><MarkdownRenderer inline>{value}</MarkdownRenderer></span>
            </button>
          ))}
        </div>

        {isDiagnostic && (
          <button
            className={`ka-not-covered ${currentAnswer === 'NOT_COVERED' ? 'selected' : ''}`}
            onClick={() => handleAnswerSelect(currentQuestion.id, 'NOT_COVERED')}
          >
            <span className="ka-option-letter ka-skip-letter">🙋</span>
            <span className="ka-option-text">I haven't learned this yet</span>
          </button>
        )}

        {error && <div className="ka-error">{error}</div>}
      </main>

      {/* Navigation footer */}
      <footer className="ka-footer">
        <button
          className="ka-nav-btn ka-prev"
          onClick={handlePreviousQuestion}
          disabled={currentQuestionIndex === 0}
        >
          ← Prev
        </button>

        <button
          className="ka-nav-btn ka-submit-mid"
          onClick={handleSubmitAssessment}
          disabled={getTotalRespondedCount() === 0}
        >
          Submit ({getTotalRespondedCount()}/{questions.length})
        </button>

        {currentQuestionIndex < questions.length - 1 ? (
          <button className="ka-nav-btn ka-next" onClick={handleNextQuestion}>
            Next →
          </button>
        ) : (
          <button
            className="ka-nav-btn ka-submit-final"
            onClick={handleSubmitAssessment}
            disabled={getTotalRespondedCount() === 0}
          >
            Submit ✓
          </button>
        )}
      </footer>
    </div>
  );
};

export default TakeAssessment;
